import React, { useState, useCallback } from 'react';
import api from '../utils/api';
import { CatalogContext } from './complexContexts';
import { ObjectMetadataValue } from '../types/ObjectHierarchicalMetadataValue';
import { logger } from '../utils/logger';

// Type definitions for each table
export interface CatalogItem {
  userId: string;
  catalogItemId: string;
  status: 'PENDING' | 'PROCESSING' | 'READY' | 'FAILED';
  createdAt: string;
  updatedAt: string;
  visibility: 'public' | 'private';
  coverImageId?: string;
  aliCatalogNumber?: string;
  isLastCatalogItem?: boolean;
  title?: string;  // Title from CatalogDescriptions (optional)
  creator?: string;  // Primary creator from CatalogDescriptions (optional)
  images: Array<{
    imageId: string;
    filename: string;
    size: number;
    mimeType: string;
    s3Key: string;
    status: string;
    url?: string;
    uploadId?: string;
    visibility?: 'public' | 'private';
    // Internal-only per-image upload timestamp (issue #1112). Present on
    // authenticated responses; scrubbed to null on public responses by
    // catalog_service._build_images_array.
    uploadedAt?: string | null;
    renditions?: Array<{
      size: string;
      format: string;
      s3Key: string;
      url?: string;
      dimensions: { width: number; height: number };
    }>;
  }>;
}

export interface DescriptionData {
  creators: Array<{
    name: string;
    isCurrentUser: boolean;
    notes?: string;
  }>;
  title: string;
  titleLocation?: string[];
  alternativeTitles: string[];
  titleAsWritten?: string;
  signatures: Array<{
    type: string[];
    location: string[];
    display: string;
  }>;
  creationDate: {
    type: string;
    value: string;
    location?: string[];
    display?: string;
  };
  workType: ObjectMetadataValue | null;
  hasMultiples: boolean;
  editions: Array<{
    type: string;
    count: number;
    total: number;
  }>;
  techniques: ObjectMetadataValue[];
  materials: ObjectMetadataValue[];
  substrate: ObjectMetadataValue[];
  mediaDescription?: string;
  mediaAdditionalComments?: string;
  stepNotes: Record<string, string[]>;
}

export interface MeasurementData {
  unit: string;
  dimensions: Array<{
    type: string;
    value: string;
    fraction: string;
    unit?: string; // For weight/mass/volume measurements
  }>;
  hasDifferentSubstrate: boolean;
  substrateMeasurements?: {
    unit: string;
    dimensions: Array<{
      type: string;
      value: string;
      fraction: string;
      unit?: string; // For weight/mass/volume measurements
    }>;
  };
  stepNotes: Record<string, string[]>;
}

export interface KeywordData {
  aliCatalogNumber: string;
  customCatalogNumbers: string[];
  keywords: string[];
  stepNotes: Record<string, string[]>;
}

export interface ProvenanceRecord {
  recordId: string;
  timestamp: string;
  publicRendering: string;
  internalRendering: string;
  createdBy: string;
  chainPosition: number;
  isPublic: boolean;
}

export interface ExhibitionData {
  type: 'exhibition' | 'publication' | 'award';
  order: number;
  exhibitionType?: string;
  link?: string;
  publicationText?: string;
}

export interface Valuation {
  /** Free-text amount string — preserves blank/in-progress entries on round-trip. */
  value: string;
  /** ISO 4217 code (e.g. USD, EUR) or a custom label. */
  currency: string;
  /** Free-text valuation type, e.g. "Insurance", "Custom appraisal". */
  type: string;
}

export interface AdditionalInfoData {
  relatedWorks: string[];
  publicNotes: string[];
  privateNotes: string[];
  physicalLocation: string;
  /** Legacy single-amount field. Retained for backwards compatibility. */
  estimatedValue: string;
  /** Multi-row valuations (Private Only > Valuation). */
  valuations?: Valuation[];
  /** Lock flag set when the user marks the work as sold. */
  markedAsSold?: boolean;
  /** Free-text condition / conservation notes. */
  conditionNotes?: string;
  exhibitions?: ExhibitionData[];
  stepNotes: Record<string, string[]>;
}



const CatalogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [catalogItems, setCatalogItems] = useState<CatalogItem[]>([]);
  const [currentItem, setCurrentItem] = useState<CatalogItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Core operations
  const createCatalogItem = useCallback(async (data: Partial<CatalogItem>): Promise<string> => {
    if (!api) throw new Error('API not configured');

    setIsLoading(true);
    setError(null);
    try {
      const response = await api.post('/api/catalog-items', data);
      const catalogItemId = response.data.catalogItemId;

      // Add to local state
      const newItem: CatalogItem = {
        userId: data.userId || '',
        catalogItemId,
        status: 'PENDING',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        visibility: 'private',
        images: [],
        ...data
      };
      setCatalogItems(prev => [...prev, newItem]);
      setCurrentItem(newItem);

      return catalogItemId;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getCatalogItem = useCallback(async (catalogItemId: string, ownerId?: string | null): Promise<CatalogItem> => {
    if (!api) throw new Error('API not configured');

    setIsLoading(true);
    setError(null);
    try {
      const params = ownerId ? { owner_id: ownerId } : undefined;
      const response = await api.get(`/api/catalogs/${catalogItemId}`, { params });
      const item = response.data;
      setCurrentItem(item);
      return item;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // GH#1177: public-aware read for related-works thumbnails.
  // Uses /api/catalogs/{id}/related-thumbnail (owned OR public) instead of
  // the owner-scoped /api/catalogs/{id} so that non-owners can see thumbnails
  // for public related items. The existing getCatalogItem remains untouched.
  const getRelatedItemThumbnail = useCallback(async (catalogItemId: string): Promise<CatalogItem> => {
    if (!api) throw new Error('API not configured');

    try {
      const response = await api.get(`/api/catalogs/${catalogItemId}/related-thumbnail`);
      return response.data;
    } catch (err: unknown) {
      // Preserve the original axios error (and its HTTP status) like getCatalogItem;
      // the consuming component only renders a placeholder, but keeping the original
      // error makes failures debuggable instead of flattening them to a bare message.
      throw err instanceof Error ? err : new Error('Unknown error');
    }
  }, []);

  const updateCatalogItem = useCallback(async (catalogItemId: string, data: Partial<CatalogItem>): Promise<void> => {
    if (!api) throw new Error('API not configured');

    setIsLoading(true);
    setError(null);
    try {
      await api.put(`/api/catalog-items/${catalogItemId}`, data);

      // Update local state
      setCatalogItems(prev =>
        prev.map(item =>
          item.catalogItemId === catalogItemId
            ? { ...item, ...data, updatedAt: new Date().toISOString() }
            : item
        )
      );

      setCurrentItem(prev =>
        prev?.catalogItemId === catalogItemId
          ? { ...prev, ...data, updatedAt: new Date().toISOString() }
          : prev
      );
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Multi-table operations
  const saveDescription = useCallback(async (catalogItemId: string, data: DescriptionData, ownerId?: string | null): Promise<void> => {
    if (!api) throw new Error('API not configured');

    setError(null);
    try {
      const config = ownerId ? { params: { owner_id: ownerId } } : undefined;
      await api.post(`/api/descriptions/${catalogItemId}`, data, config);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string }; status?: number }; message?: string; config?: { url?: string } };
      logger.error('Failed to save description', {
        catalogItemId,
        error: error.response?.data || error.message,
        status: error.response?.status,
        url: error.config?.url
      });
      setError(error.response?.data?.message || error.message || 'Unknown error');
      throw err;
    }
  }, []);

  const getDescription = useCallback(async (catalogItemId: string, ownerId?: string | null): Promise<DescriptionData> => {
    if (!api) throw new Error('API not configured');

    setError(null);
    try {
      const params = ownerId ? { owner_id: ownerId } : undefined;
      const response = await api.get(`/api/descriptions/${catalogItemId}`, { params });
      return response.data;
    } catch (err: unknown) {
      const error = err as { response?: { status?: number }; message?: string };
      // Handle 404 as "no existing description" rather than an error
      if (error.response?.status === 404) {
        // Return default empty description structure
        return {
          creators: [],
          title: '',
          alternativeTitles: [],
          signatures: [],
          creationDate: { type: '', value: '' },
          workType: null,
          hasMultiples: false,
          editions: [],
          techniques: [],
          materials: [],
          substrate: [],
          stepNotes: {}
        };
      }
      setError(error.message || 'Unknown error');
      throw err;
    }
  }, []);

  const saveMeasurements = useCallback(async (catalogItemId: string, data: MeasurementData, ownerId?: string | null): Promise<void> => {
    if (!api) throw new Error('API not configured');

    setError(null);
    try {
      const config = ownerId ? { params: { owner_id: ownerId } } : undefined;
      await api.post(`/api/measurements/${catalogItemId}`, data, config);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const getMeasurements = useCallback(async (catalogItemId: string, ownerId?: string | null): Promise<MeasurementData> => {
    if (!api) throw new Error('API not configured');

    setError(null);
    try {
      const params = ownerId ? { owner_id: ownerId } : undefined;
      const response = await api.get(`/api/measurements/${catalogItemId}`, { params });
      return response.data;
    } catch (err: unknown) {
      const error = err as { response?: { status?: number }; message?: string };
      // Handle 404 as "no existing measurements" rather than an error
      if (error.response?.status === 404) {
        // Return default empty measurements structure
        return {
          unit: 'inches',
          dimensions: [],
          hasDifferentSubstrate: false,
          stepNotes: {}
        };
      }
      setError(error.message || 'Unknown error');
      throw err;
    }
  }, []);

  const saveKeywords = useCallback(async (catalogItemId: string, data: KeywordData, ownerId?: string | null, catalogAliId?: string | null): Promise<{ aliCatalogNumber?: string }> => {
    if (!api) throw new Error('API not configured');

    setError(null);
    try {
      // Build params only with non-null values so the backend sees ``owner_id``
      // and/or ``catalogAliId`` only when meaningful — matches the wire shape
      // the existing CatalogContext.ownerIdOnSaves tests pin. When neither is
      // set, config is undefined so the axios call signature stays identical
      // to the legacy path (older callers continue to work unchanged).
      const params: Record<string, string> = {};
      if (ownerId) params.owner_id = ownerId;
      if (catalogAliId) params.catalogAliId = catalogAliId;
      const config = Object.keys(params).length > 0 ? { params } : undefined;
      const response = await api.post(`/api/keywords/${catalogItemId}`, data, config);
      return { aliCatalogNumber: response.data?.aliCatalogNumber };
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const getKeywords = useCallback(async (catalogItemId: string, ownerId?: string | null): Promise<KeywordData> => {
    if (!api) throw new Error('API not configured');

    setError(null);
    try {
      const params = ownerId ? { owner_id: ownerId } : undefined;
      const response = await api.get(`/api/keywords/${catalogItemId}`, { params });
      return response.data;
    } catch (err: unknown) {
      const error = err as { response?: { status?: number }; message?: string };
      // Handle 404 as "no existing keywords" rather than an error
      if (error.response?.status === 404) {
        // Return default empty keywords structure
        return {
          aliCatalogNumber: '',
          customCatalogNumbers: [],
          keywords: [],
          stepNotes: {}
        };
      }
      setError(error.message || 'Unknown error');
      throw err;
    }
  }, []);

  const saveAdditionalInfo = useCallback(async (catalogItemId: string, data: AdditionalInfoData, ownerId?: string | null): Promise<void> => {
    if (!api) throw new Error('API not configured');

    setError(null);
    try {
      const config = ownerId ? { params: { owner_id: ownerId } } : undefined;
      await api.post(`/api/additional-info/${catalogItemId}`, data, config);
    } catch (err: unknown) {
      logger.error('saveAdditionalInfo: Got error', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const getAdditionalInfo = useCallback(async (catalogItemId: string, ownerId?: string | null): Promise<AdditionalInfoData> => {
    if (!api) throw new Error('API not configured');

    setError(null);
    try {
      const params = ownerId ? { owner_id: ownerId } : undefined;
      const response = await api.get(`/api/additional-info/${catalogItemId}`, { params });
      return response.data;
    } catch (err: unknown) {
      const error = err as { response?: { status?: number }; message?: string };
      // Handle 404 as "no existing additional info" rather than an error
      if (error.response?.status === 404) {
        // Return default empty additional info structure
        return {
          relatedWorks: [],
          publicNotes: [],
          privateNotes: [],
          physicalLocation: '',
          estimatedValue: '',
          valuations: [],
          markedAsSold: false,
          conditionNotes: '',
          stepNotes: {}
        };
      }
      logger.error('getAdditionalInfo: Got error', err);
      setError(error.message || 'Unknown error');
      throw err;
    }
  }, []);

  // saveExhibitions/getExhibitions must be declared after getAdditionalInfo/saveAdditionalInfo
  // because they reference them in their useCallback dependency arrays
  const saveExhibitions = useCallback(async (catalogItemId: string, data: ExhibitionData[], stepNotes?: Record<string, string[]>, ownerId?: string | null): Promise<void> => {
    if (!api) throw new Error('API not configured');

    setError(null);
    try {
      // Save exhibitions as part of additional info for now
      const additionalInfo = await getAdditionalInfo(catalogItemId, ownerId);
      // Strip response-only fields that the POST endpoint rejects (extra="forbid")
      const { userId: _u, catalogItemId: _c, updatedAt: _t, ...saveData } =
        additionalInfo as AdditionalInfoData & { userId?: string; catalogItemId?: string; updatedAt?: string };
      await saveAdditionalInfo(catalogItemId, {
        ...saveData,
        exhibitions: data,
        ...(stepNotes !== undefined ? { stepNotes } : {})
      }, ownerId);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      throw err;
    }
  }, [getAdditionalInfo, saveAdditionalInfo]);

  const getExhibitions = useCallback(async (catalogItemId: string, ownerId?: string | null): Promise<{ exhibitions: ExhibitionData[]; stepNotes: Record<string, string[]> }> => {
    if (!api) throw new Error('API not configured');

    setError(null);
    try {
      // Get exhibitions from additional info for now
      const additionalInfo = await getAdditionalInfo(catalogItemId, ownerId);
      const typed = additionalInfo as AdditionalInfoData & { exhibitions?: ExhibitionData[] };
      return {
        exhibitions: typed.exhibitions || [],
        stepNotes: typed.stepNotes || {},
      };
    } catch (err: unknown) {
      const error = err as { response?: { status?: number }; message?: string };
      // Handle 404 as "no existing exhibitions" rather than an error
      if (error.response?.status === 404) {
        return { exhibitions: [], stepNotes: {} };
      }
      setError(error.message || 'Unknown error');
      throw err;
    }
  }, [getAdditionalInfo]);

  // Provenance operations
  const getProvenanceChain = useCallback(async (catalogItemId: string, ownerId?: string | null): Promise<{ records: ProvenanceRecord[]; version: number; chainLength: number }> => {
    if (!api) throw new Error('API not configured');

    setError(null);
    try {
      const params = ownerId ? { owner_id: ownerId } : undefined;
      const response = await api.get(`/api/provenance/${catalogItemId}`, { params });
      return {
        records: response.data.records || [],
        version: response.data.version ?? 0,
        chainLength: response.data.chainLength ?? 0,
      };
    } catch (err: unknown) {
      const error = err as { response?: { status?: number }; message?: string };
      // Handle 404 as "no existing provenance" rather than an error
      if (error.response?.status === 404) {
        return { records: [], version: 0, chainLength: 0 };
      }
      setError(error.message || 'Unknown error');
      throw err;
    }
  }, []);

  const addProvenanceRecord = useCallback(async (catalogItemId: string, data: Partial<ProvenanceRecord>, ownerId?: string | null): Promise<void> => {
    if (!api) throw new Error('API not configured');

    setError(null);
    try {
      const config = ownerId ? { params: { owner_id: ownerId } } : undefined;
      await api.post(`/api/provenance/${catalogItemId}`, data, config);
    } catch (err: unknown) {
      logger.error('addProvenanceRecord: Got error', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const commitProvenanceRecords = useCallback(async (
    catalogItemId: string,
    records: Array<{ publicRendering: string; internalRendering: string; order: number; isPublic?: boolean }>,
    ownerId?: string | null
  ): Promise<{
    committedRecords: ProvenanceRecord[];
    chainLength: number;
  }> => {
    if (!api) throw new Error('API not configured');

    setError(null);
    try {
      const config = ownerId ? { params: { owner_id: ownerId } } : undefined;
      const response = await api.post(`/api/provenance/${catalogItemId}/commit`, { records }, config);
      return response.data;
    } catch (err: unknown) {
      logger.error('commitProvenanceRecords: Got error', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const replaceProvenanceRecords = useCallback(async (
    catalogItemId: string,
    records: Array<{ recordId?: string; publicRendering: string; internalRendering: string; order: number; isPublic?: boolean }>,
    expectedVersion?: number,
    ownerId?: string | null
  ): Promise<{
    records: ProvenanceRecord[];
    chainLength: number;
    version: number;
  }> => {
    if (!api) throw new Error('API not configured');

    setError(null);
    try {
      const payload: { records: typeof records; expectedVersion?: number } = { records };
      if (expectedVersion !== undefined) {
        payload.expectedVersion = expectedVersion;
      }
      const config = ownerId ? { params: { owner_id: ownerId } } : undefined;
      const response = await api.put(`/api/provenance/${catalogItemId}`, payload, config);
      return response.data;
    } catch (err: unknown) {
      logger.error('replaceProvenanceRecords: Got error', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const getAllCatalogItems = useCallback(
    async (
      ownerId?: string | null,
      catalogAliId?: string | null,
    ): Promise<CatalogItem[]> => {
      if (!api) throw new Error('API not configured');

      setIsLoading(true);
      setError(null);
      try {
        // PR 4b — both filters can apply: ownerId scopes to a delegator,
        // catalogAliId scopes to one of the user's (or delegator's) owned
        // catalogs. Either, both, or neither may be set.
        const params: Record<string, string> = {};
        if (ownerId) params.owner_id = ownerId;
        if (catalogAliId) params.catalog_ali_id = catalogAliId;
        const response = await api.get('/api/catalogs', {
          params: Object.keys(params).length > 0 ? params : undefined,
        });
      const items = response.data.artwork.map((item: { artworkId: string; status: string; createdAt: string; updatedAt: string; title?: string; creator?: string; aliCatalogNumber?: string }) => ({
        userId: '',
        catalogItemId: item.artworkId,
        status: item.status as 'PENDING' | 'PROCESSING' | 'READY' | 'FAILED',
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        visibility: 'private' as const,
        images: [],
        title: item.title,
        creator: item.creator,
        aliCatalogNumber: item.aliCatalogNumber,
      }));
      setCatalogItems(items);
      return items;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <CatalogContext.Provider value={{
      // Core operations
      createCatalogItem,
      getCatalogItem,
      getRelatedItemThumbnail,
      updateCatalogItem,
      getAllCatalogItems,

      // Multi-table operations
      saveDescription,
      getDescription,
      saveMeasurements,
      getMeasurements,
      saveKeywords,
      getKeywords,
      saveExhibitions,
      getExhibitions,
      saveAdditionalInfo,
      getAdditionalInfo,

      // Provenance operations
      getProvenanceChain,
      addProvenanceRecord,
      commitProvenanceRecords,
      replaceProvenanceRecords,

      // State management
      catalogItems,
      currentItem,
      isLoading,
      error,
      setCurrentItem
    }}>
      {children}
    </CatalogContext.Provider>
  );
};

export { CatalogProvider };
